// Shared display helpers for the Transformers Pinball Championship pages.

import autobotsIcon from '../assets/team-icons/autobots.png';
import decepticonsIcon from '../assets/team-icons/decepticons.png';
import quintessonsIcon from '../assets/team-icons/quintessons.png';
import { personName } from './personName';

export const TEAMS = [
  { id: 'autobots', name: 'Autobots', icon: autobotsIcon, color: '#ff4040' },
  { id: 'decepticons', name: 'Decepticons', icon: decepticonsIcon, color: '#a855f7' },
  { id: 'quintessons', name: 'Quintessons', icon: quintessonsIcon, color: '#00d4aa' }
];

export function getTeamMeta(teamId) {
  return TEAMS.find(team => team.id === teamId) || { id: teamId, name: teamId, icon: null, color: '#00c8ff' };
}

// Small <img> for a team's faction icon - use anywhere the old emoji was inline in text
// (badges, headers, roster rows). Sized via className/style on the wrapping element.
export function TeamIcon({ teamId, size = 18, className = '', style = {} }) {
  const meta = getTeamMeta(teamId);
  if (!meta.icon) return null;
  return (
    <img
      src={meta.icon}
      alt={meta.name}
      className={className}
      style={{ height: size, width: 'auto', verticalAlign: 'middle', ...style }}
    />
  );
}

// Matches the TV dashboard's battery color grading (TransformersTvDashboard.js/.css):
// >60% high (cyan), >35% healthy (green), >15% warning (amber), else critical (red).
export function getBatteryStatusClass(battery, isStasisLocked) {
  if (isStasisLocked) return 'locked';
  if (battery === null || battery === undefined) return 'high';
  if (battery <= 15) return 'critical';
  if (battery <= 35) return 'warning';
  if (battery <= 60) return 'healthy';
  return 'high';
}

// Roster ordering used by every view that lists a team's players (Broadcast, TV dashboard).
// Players still in contention sort above those already in Stasis Lock, each group ordered by
// score descending. "Who on my team is still in it?" is the question these rosters are actually
// read for, and a pure score sort scattered the answer among locked players.
export function compareByStasisThenScore(a, b) {
  const aLocked = !!a.isStasisLocked;
  const bLocked = !!b.isStasisLocked;
  if (aLocked !== bLocked) return aLocked ? 1 : -1;
  return (b.score || 0) - (a.score || 0);
}

// How many of a team's players have not hit Stasis Lock yet, i.e. are still in contention for
// Grand Champion. Counted over the full roster the caller passes in, deliberately not over
// whatever subset a given view chooses to render, so the number sits alongside the roster-size
// count and reads the same on every surface.
export function countInContention(members) {
  return members.filter(m => !m.isStasisLocked).length;
}

// Moved to utils/formatScore.js (shared, feature-agnostic location) so other features
// (e.g. PinGolf) can reuse it without importing from this Transformers-specific file.
// Re-exported here so existing imports from '../utils/transformersCompetition' keep working.
export { formatScoreAbbreviated } from './formatScore';

export function formatPercent(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return `${Number(value).toFixed(digits)}%`;
}

// Mirrors MAX_WEEKLY_BATTERY_LOSS_PCT / computeBatteryLoss in
// server/src/services/transformersFormulas.js. Completed weeks already carry a stored
// batteryLoss, but the in-progress week has none yet (the server only writes it when the round
// closes), so the same formula runs client-side against the current best score to show players
// what the week is costing them right now.
export const MAX_WEEKLY_BATTERY_LOSS_PCT = 34;

export function projectBatteryLoss(target, score) {
  const targetValue = Number(target);
  if (!targetValue || targetValue <= 0 || Number.isNaN(targetValue)) return null;
  const scoreValue = Number(score) > 0 ? Number(score) : 0;
  const missPct = Math.max(((targetValue - scoreValue) / targetValue) * 100, 0);
  return Math.min((missPct / 100) * MAX_WEEKLY_BATTERY_LOSS_PCT, MAX_WEEKLY_BATTERY_LOSS_PCT);
}

export function getDisplayName(person) {
  if (!person) return 'Unknown player';
  return personName(person) || person.username || person.sternUsername || 'Unknown player';
}

// Reads a UTC instant's wall-clock fields (weekday/date/time) as they appear in America/New_York,
// via the "reparse a timeZone-formatted string as local" trick - safe here because we only ever
// read fields back out with local getters (getDay/getHours/etc.), never treat the object's actual
// instant as meaningful.
function easternWallClock(date) {
  return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
}

// The real UTC-vs-America/New_York offset (minutes) at a given instant, derived by diffing the
// same instant formatted in both zones - correctly DST-aware without a date library.
function easternOffsetMs(date) {
  const utcAsLocal = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const etAsLocal = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return utcAsLocal.getTime() - etAsLocal.getTime();
}

// The competition week auto-advance cron runs every Friday at 2:00 AM Eastern (see
// competitionScheduler.js's scheduleWeekAutoAdvance) - this finds the next occurrence of that
// moment from "now", so every page showing a countdown/deadline derives it from the same single
// source of truth as the actual schedule, rather than a hardcoded label that could drift.
export function getNextRoundCloseTime(now = new Date()) {
  const wallNow = easternWallClock(now);
  const FRIDAY = 5;
  let daysUntilFriday = (FRIDAY - wallNow.getDay() + 7) % 7;
  const pastCloseToday = wallNow.getDay() === FRIDAY
    && (wallNow.getHours() > 2 || (wallNow.getHours() === 2 && wallNow.getMinutes() > 0));
  if (daysUntilFriday === 0 && pastCloseToday) daysUntilFriday = 7;

  const targetWall = new Date(wallNow);
  targetWall.setDate(targetWall.getDate() + daysUntilFriday);
  targetWall.setHours(2, 0, 0, 0);

  const naiveUtcGuess = new Date(Date.UTC(
    targetWall.getFullYear(), targetWall.getMonth(), targetWall.getDate(),
    targetWall.getHours(), targetWall.getMinutes(), 0
  ));
  return new Date(naiveUtcGuess.getTime() + easternOffsetMs(naiveUtcGuess));
}

// "2d 14h", "6h 20m", "12m" - deliberately coarse (two units max), since this is a "how much
// time do I have left" glance, not a precise clock.
export function formatTimeRemaining(targetDate, now = new Date()) {
  const diffMs = targetDate.getTime() - now.getTime();
  if (diffMs <= 0) return 'Closing soon';
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
